/**
 * 로그인 연장 팝업.
 *
 * 서버가 `#nx-session-guard[data-exp]` 로 만료 시각(epoch 초)을 내려준다.
 *   · exp - warn 시점에 팝업을 띄우고 남은 시간을 카운트다운
 *   · [로그인 연장] → POST /session/extend → 새 exp 로 타이머 리셋
 *   · exp 가 지나면 /login?session=expired 로 이동
 *
 * ★ 이 스크립트는 편의 기능이다. 없거나 실패해도 서버가 만료를 강제한다.
 * ★ 노트북을 닫았다 열면 타이머가 밀릴 수 있으므로, 팝업을 띄우기 직전에
 *   /session/state 로 실제 만료 시각을 한 번 확인한다.
 */
const TICK_MS = 1000;

/**
 * ★★★★★★ 다른 곳에서 로그인했을 때 **가만히 있는 화면도 알아채게** 하는 주기.
 *   (사장님 물음 2026-08-21: *"한군데서 로그인했다가 다른 데서 로그인하면 먼저
 *    로그인한 데서 경고를 주고 로그아웃하는 게 실제 작동하는가?"* — **반쪽만
 *    작동했다.**)
 *
 *   옛 판은 `/session/state` 를 **만료 5분 전에 딱 한 번**만 물었다. 그래서
 *   서버는 이미 «끊겼다» 를 알고 있는데, 화면은 최대 **55분** 동안 멀쩡해
 *   보였다. 그 사람이 무언가 누르면 그때야 튕겼다.
 *
 *   ★ 이것은 **판정을 시간에 거는 것이 아니다**(0-9 규칙 7). 판정은 서버가
 *     **사실**로 한다(`sessions.session_conflict` — 쿠키의 sid ↔ users.session_id).
 *     여기서 정하는 것은 «얼마나 빨리 알아채는가» 뿐이다.
 *   ★ `/session/state` 는 `tenancy.NO_TOUCH_PATHS` 라 **유휴 만료를 밀지
 *     않는다** — 탭을 열어 둔 것만으로 세션이 무한 연장되지 않는다.
 *   ★ 탭이 **안 보이면 쉰다** — 보고 있지도 않은 화면 때문에 요청을 내지 않는다.
 */
const CHECK_MS = 60_000;

/**
 * «다른 기기에서 로그인되었습니다» 를 **몇 ms 보여 주고** 로그인 화면으로 갈까.
 * 사람이 읽을 시간은 주되, 자리에 없으면 스스로 간다.
 * ★ 판정이 아니라 **보여 주는 시간**이다(0-9 규칙 10 — 꾸미기는 시간을 써도 된다).
 */
const KICKED_HOLD_MS = 8000;

export function initSessionGuard(): void {
  const root = document.getElementById('nx-session-guard');
  if (!root) return;

  const warn = Number(root.dataset.warn || '300');
  const extendUrl = root.dataset.extendUrl || '/session/extend';
  const stateUrl = root.dataset.stateUrl || '/session/state';
  const loginUrl = root.dataset.loginUrl || '/login?session=expired';
  const remainEl = root.querySelector<HTMLElement>('[data-nx-session-remain]');
  const btnExtend = root.querySelector<HTMLButtonElement>('[data-nx-session-extend]');
  const btnLogout = root.querySelector<HTMLButtonElement>('[data-nx-session-logout]');

  let exp = Number(root.dataset.exp || '0');
  let shown = false;
  let checking = false;

  const now = (): number => Math.floor(Date.now() / 1000);
  const show = (): void => { root.classList.remove('hidden'); shown = true; };
  const hide = (): void => { root.classList.add('hidden'); shown = false; };
  // reason: 'kicked'(다른 곳에서 로그인) | 'expired'(시간 만료).
  // 서버가 알려준 사유를 그대로 로그인 화면에 넘겨 맞는 문구를 띄운다.
  const go = (reason?: string): void => {
    window.location.href = reason
      ? loginUrl.replace(/session=[^&]*/, `session=${encodeURIComponent(reason)}`)
      : loginUrl;
  };

  /** 팝업의 얼굴을 바꾼다 — 'warn'(곧 만료) / 'kicked'(다른 기기에서 로그인). */
  const face = (mode: 'warn' | 'kicked'): void => {
    root.querySelectorAll<HTMLElement>('[data-nx-when]').forEach((el) => {
      el.hidden = el.dataset.nxWhen !== mode;
    });
  };

  /**
   * ★★★★★★ 사장님 지시 2026-08-21: *"첫 기기에 다른 기기에서 로그인하여
   * 로그아웃된다는 **경고** 줘야지."*
   *
   * 옛 판은 **말없이 로그인 화면으로 튕겼다.** 쓰던 사람 입장에서는 화면이
   * 갑자기 바뀐 것이라, 무슨 일이 났는지 알 수가 없었다.
   * ⇒ 이제 **그 자리에서 먼저 말한다**: 「다른 기기에서 로그인되었습니다 —
   *   이 기기는 로그아웃됩니다」. 그리고 [로그인 화면으로] 를 누르면 간다.
   *
   * ★ 자리에 없을 수도 있으니 **잠시 뒤 스스로도 간다**(KICKED_HOLD_MS).
   *   이건 «꾸미기» 자리다(0-9 규칙 10) — **판정은 이미 서버가 끝냈고**
   *   이 세션은 어차피 죽었다. 여기서 정하는 것은 «얼마나 보여 주는가» 뿐이다.
   * ★ [연장] 은 **숨긴다** — 연장할 수 있는 세션이 아니다. 보이면 사람이
   *   «되는 줄 알고» 누르고, 실패하고, 또 누른다.
   */
  let leaving = false;
  const bail = (reason?: string): void => {
    if (leaving) return;
    leaving = true;
    if (reason !== 'kicked') { go(reason); return; }
    face('kicked');
    show();
    window.setTimeout(() => go('kicked'), KICKED_HOLD_MS);
  };

  const fmt = (sec: number): string => {
    const s = Math.max(0, sec);
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  };

  async function resync(): Promise<void> {
    if (checking) return;
    checking = true;
    try {
      const r = await fetch(stateUrl, { credentials: 'same-origin' });
      // ★ 상태를 먼저 본다. 500 이면 본문이 HTML 이라 `r.json()` 이 예외를 내고
      //   아래 catch 로 빠지는데, 그러면 **낡은 exp 로 계속 도는** 상태가 된다.
      //   401/403 은 세션이 이미 끊긴 것이므로 바로 알린다.
      if (r.status === 401 || r.status === 403) { bail('expired'); return; }
      if (!r.ok) return; // 서버 오류 — 다음 tick 에서 다시 본다
      const j = (await r.json()) as { ok?: boolean; exp?: number; reason?: string };
      if (!j.ok) { bail(j.reason); return; }
      if (j.exp) exp = j.exp;
    } catch {
      /* 네트워크 실패는 무시 — 다음 tick 에서 다시 본다 */
    } finally {
      checking = false;
    }
  }

  async function extend(): Promise<void> {
    if (btnExtend) btnExtend.disabled = true;
    try {
      // 본문이 없는 POST 라 서버가 볼 것은 헤더뿐이다.
      const r = await fetch(extendUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { ...(window.nx?.csrfHeaders?.() ?? {}) },
      });
      if (r.status === 401 || r.status === 403) { bail('expired'); return; }
      if (!r.ok) return; // 연장 실패 — 팝업을 유지해 사용자가 다시 누를 수 있게 둔다
      const j = (await r.json()) as { ok?: boolean; exp?: number; reason?: string };
      if (!j.ok) { bail(j.reason); return; }
      if (j.exp) exp = j.exp;
      hide();
    } catch {
      /* 실패하면 팝업을 유지한다 */
    } finally {
      if (btnExtend) btnExtend.disabled = false;
    }
  }

  // ★★★★★ 폴러가 **401/403** 을 만나면 여기로 알린다(`_nx_poll.html`).
  //   옛 판은 그 401 을 그냥 «실패» 로 세고 열 번 뒤 조용히 죽었다 — 화면에는
  //   「다른 곳에서 로그인」이 아니라 **진행 막대가 그냥 멈춘 것**으로 보였다.
  //   ★ 사유 판정을 폴러에 베끼지 않는다 — 서버에 한 번 물어 **여기 한 곳**에서
  //     'kicked' 와 'expired' 를 가른다(F221: 규칙은 한 자리).
  window.addEventListener('nx:session:lost', () => {
    lastCheck = now();
    void resync();
  });

  root.querySelector<HTMLButtonElement>('[data-nx-session-golo]')
      ?.addEventListener('click', () => { go('kicked'); });

  btnExtend?.addEventListener('click', () => { void extend(); });
  btnLogout?.addEventListener('click', () => {
    const f = document.createElement('form');
    f.method = 'post';
    f.action = '/logout';
    document.body.appendChild(f);
    f.submit();
  });

  let lastCheck = now();

  window.setInterval(() => {
    if (!exp) return;
    // ★ 주기 확인 — **다른 곳에서 로그인**을 가만히 있는 화면도 알아챈다.
    //   `resync()` 가 서버에서 `{ok:false, reason:'kicked'}` 를 받으면 그 자리에서
    //   로그인 화면으로 보낸다(사유는 서버가 준 것을 그대로 쓴다).
    if (!document.hidden && now() - lastCheck >= CHECK_MS / 1000) {
      lastCheck = now();
      void resync();
    }
    const left = exp - now();
    if (left <= 0) {
      // 정말 만료인지 서버에 확인한 뒤 이동(절전에서 깨어난 경우 방어)
      void resync().then(() => { if (exp - now() <= 0) bail('expired'); });
      return;
    }
    if (leaving) return;          // ★ 쫓겨난 팝업이 떠 있으면 덮지 않는다
    if (left <= warn) {
      if (!shown) { void resync(); face('warn'); show(); }
      if (remainEl) remainEl.textContent = fmt(exp - now());
    } else if (shown) {
      hide();
    }
  }, TICK_MS);
}
